import { Controller } from '@hotwired/stimulus';
import Axios from '../axios';
import { getMeta } from '../helpers';

export default class extends Controller {
    setupFormAction(action, actionData) {
        switch (action) {
            case 'reset_fields':
                this.resetFields(actionData);
                break;

            case 'show_popup':
                this.showPopup(actionData);
                break;

            case 'register_workout_success':
                this.registerWorkoutSuccess(actionData);
                break;

            case 'replace_form':
                this.replaceForm(actionData);
                break;

            case 'update_price':
                this.updatePrice(actionData);
                break;

            case 'update_timetable_form':
                this.updateTimetableForm(actionData);
                break;
        }
    }

    connect() {
        this.numberTypeInputRegex();
        this.enableContractCheckboxWhenFilled();

        this.element.addEventListener('submit', async (e) => {
            e.preventDefault();

            if (this.element.classList.contains('loading')) {
                return;
            }

            this.element.classList.add('loading');
            this.removeFormNotes();

            const url = this.element.action;
            const method = this.element.method.toLowerCase();
            const data = new FormData(this.element);

            let response = null;

            try {
                if (method === 'get') {
                    response = await Axios.get(url);
                } else if (method === 'post') {
                    response = await this.handlePostSubmit(url, data, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                }

                response = response.data.data;
            } catch (error) {
                response = error.response.data.data;
            }

            if (response && response.action) {
                if (Array.isArray(response.action)) {
                    response.action.forEach((action) => {
                        this.setupFormAction(action, response.action_data[action]);
                    });
                } else {
                    this.setupFormAction(response.action, response.action_data);
                }
            }

            if (response && response.errors && Object.keys(response.errors).length) {
                this.setupFormErrors(response.errors);
            } else if (response && response.general_error) {
                this.setupFormGeneralError(response.general_error);
            } else if (response && response.general_success) {
                this.setupFormGeneralSuccess(response.general_success);
            } else if (response && response.redirect) {
                window.location.href = response.redirect;
            } else if (response && response.reload) {
                window.location.reload();
            } else if (!response.action) {
                this.setupFormUnexpectedError();
            }
        });
    }

    handlePostSubmit(url, data, config) {
        if (window.grecaptcha && data instanceof FormData) {
            return new Promise((resolve) => {
                window.grecaptcha.ready(() => {
                    resolve(window.grecaptcha.execute(klark.recaptcha_site_key, { action: 'submit' }));
                });
            }).then((recaptchaToken) => {
                data.append('recaptcha', recaptchaToken);

                return Axios.post(url, data, config);
            });
        }

        return Axios.post(url, data, config);
    }

    setupFormUnexpectedError() {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = getMeta('global_axios_validator_error');
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormGeneralSuccess(success) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-success');
        messageNode.innerHTML = success;
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormGeneralError(error) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = error;
        this.element.appendChild(messageNode);
        this.element.classList.remove('loading');
    }

    setupFormErrors(errors) {
        let topMostError = null;

        for (let i = 0, keys = Object.keys(errors), ii = keys.length; i < ii; i++) {
            const input = this.element.querySelector(`[name='${keys[i]}']`);

            if (input) {
                input.classList.add('invalid');
                const reservedErrorElement = keys[i].includes('[') ? null : this.element.querySelector(`span.input-error--${keys[i]}`);

                if (reservedErrorElement) {
                    reservedErrorElement.innerHTML = errors[keys[i]];
                    reservedErrorElement.style.display = 'inline-block';
                } else {
                    const messageNode = document.createElement('span');
                    messageNode.classList.add('input-error');
                    messageNode.innerHTML = errors[keys[i]];

                    if (input.classList.contains('checkbox-field')) {
                        input.parentElement.parentNode.insertBefore(messageNode, input.parentElement.nextSibling);
                    } else {
                        input.parentNode.insertBefore(messageNode, input.nextSibling);
                    }

                    if (topMostError) {
                        if (topMostError.getBoundingClientRect().top > input.getBoundingClientRect().top) {
                            topMostError = input;
                        }
                    } else {
                        topMostError = input;
                    }
                }
            }
        }

        this.element.classList.remove('loading');
    }

    removeFormNotes() {
        const generalErrors = this.element.querySelectorAll('.general-error');
        for (const el of generalErrors) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const generalSuccess = this.element.querySelectorAll('.general-success');
        for (const el of generalSuccess) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const fields = this.element.querySelectorAll('.input-field, .checkbox-field');
        for (const el of fields) {
            el.classList.remove('invalid');
        }

        const messageFields = this.element.querySelectorAll('.input-error');
        for (const el of messageFields) {
            el.remove();
        }
    }

    resetFields(data) {
        if (data) {
            for (const item of data) {
                this.element.querySelectorAll('[name="' + item + '"]').forEach((el) => {
                    el.value = '';
                    if (el.checked) {
                        el.checked = false;
                    }
                    if (el.type === 'file') {
                        const fileController = Stimulus.getControllerForElementAndIdentifier(el.parentElement.parentElement, 'file');
                        fileController.resetFileInput(el.parentElement.parentElement);
                    }
                });
            }
        }
    }

    showPopup(data) {
        this.element.classList.remove('loading');

        if (data) {
            const popup = document.getElementById('global-popup');

            if (popup) {
                const popupController = Stimulus.getControllerForElementAndIdentifier(popup, 'modal');

                if (popupController) {
                    const content = popup.querySelector('.modal-inner');
                    content.innerHTML = data;
                    popupController.showModal();
                }
            }
        }
    }

    registerWorkoutSuccess(data) {
        this.element.classList.remove('loading');

        if (data.availability.id && data.availability.content) {
            const container = document.getElementById(data.availability.id);

            if (container) {
                container.innerHTML = data.availability.content;
            }

        }
    }

    updateTimetableForm(data) {
        this.element.setAttribute('action', data.route);

        if (data.reservation_code) {
            const reservationCodeInput = this.element.querySelector('input[name="reservation_code"]');
            if (reservationCodeInput) {
                reservationCodeInput.value = data.reservation_code;
            }
        }

        const submitButton = this.element.querySelector('button[type="submit"]');
        if (submitButton) {
            submitButton.parentNode.innerHTML = data.submit_button;
        }
    }

    replaceForm(data) {
        this.element.innerHTML = data.html;
    }

    updatePrice(data) {
        const priceElements = document.querySelectorAll('.checkout-total');

        if (priceElements) {
            priceElements.forEach((el) => {
                const discountEl = el.querySelector('.discount');

                if (discountEl) {
                    discountEl.innerHTML = data.price;
                } else {
                    const discountNode = document.createElement('span');
                    discountNode.classList.add('discount');
                    discountNode.innerHTML = data.price;
                    el.appendChild(discountNode);
                }
            });
        }
    }

    numberTypeInputRegex() {
        const numberInputs = this.element.querySelectorAll('.only-number');

        numberInputs.forEach((input) => {
            input.addEventListener('keydown', (e) => {
                if ([8, 46, 9, 27, 13, 107, 187].indexOf(e.keyCode) !== -1 ||
                    (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) ||
                    (e.keyCode === 67 && (e.ctrlKey || e.metaKey)) ||
                    (e.keyCode === 88 && (e.ctrlKey || e.metaKey)) ||
                    (e.keyCode >= 35 && e.keyCode <= 39)) {
                    return;
                } else if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105) && e.keyCode !== 107) {
                    e.preventDefault();
                }
            });
        });
    }

    enableContractCheckboxWhenFilled() {
        const contractCheckbox = this.element.querySelector('input[name="consents[e_contract]"]');

        if (contractCheckbox) {
            const fields = this.element.querySelectorAll('input:not([type="checkbox"]), select, textarea');
            let visibleFields = Array.from(fields).filter(this.isVisibleByClass);

            let isDisabled = true;

            visibleFields.forEach((field) => {
                field.addEventListener('change', () => {
                    isDisabled = false;

                    setTimeout(() => {
                        let allVisibleFields = Array.from(fields).filter(this.isVisibleByClass);
                        allVisibleFields.forEach((f) => {
                            if (!f.value) {
                                isDisabled = true;
                            }
                        });

                        if (isDisabled) {
                            contractCheckbox.setAttribute('disabled', 'disabled');
                            contractCheckbox.checked = false;
                            contractCheckbox.classList.add('disabled');
                        } else {
                            contractCheckbox.removeAttribute('disabled');
                            contractCheckbox.classList.remove('disabled');
                        }
                    }, 200);
                });
            });
        }
    }

    isVisibleByClass(el) {
        while (el) {
            if (el.classList && el.classList.contains('d-none')) {
                return false;
            }
            el = el.parentElement;
        }
        return true;
    }
}
